import {
  Button,
  Center,
  Flex,
  Loader,
  Modal,
  ScrollArea,
  Text,
  Divider,
  Space,
} from "@mantine/core";
import { NotificationUserType } from "@prisma/client";
import Router from "next/router";
import type { FC } from "react";
import { memo, useCallback, useContext, useEffect, useState } from "react";
import { BiLike } from "react-icons/bi";
import { FaRegComment } from "react-icons/fa";
import {
  MdEventAvailable,
  MdLightbulbOutline,
  MdOutlineMilitaryTech,
  MdMessage,
} from "react-icons/md";
import { RiUserFollowLine } from "react-icons/ri";
import useInfiniteScroll from "react-infinite-scroll-hook";
import { AvatarIconMemo } from "src/component/element";
import { NotificationMessage } from "src/component/pageComponent/Notification/NotificationList/NotificationMessage";
import { NotificationMessageModal } from "src/component/pageComponent/Notification/NotificationList/NotificationMessageModal";
import { NotificationUserContext } from "src/component/pageComponent/Notification/Provider/NotificationUserProvider";
import { useGetMyNotificationsQuery } from "src/service/NotificationUserApi/NotificationUserApi";
import type { NotificationUserItem } from "src/service/NotificationUserApi/type";
import { useGetUserSettingsQuery } from "src/service/UsersApi";

import styles from "./NotificationListModal.module.scss";

const NotificationListModal: FC<{
  opened: boolean;
  onClose: () => void;
}> = ({ opened, onClose }) => {
  const [detailNotification, setDetailNotification] =
    useState<NotificationUserItem | null>();
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);
  const [cursorId, setCursorId] = useState<string>("");
  const { readNotification, onResetBadgeCount, badgeCount } = useContext(
    NotificationUserContext,
  );
  const { data: currentUser } = useGetUserSettingsQuery();
  const {
    data: notifications,
    isError,
    isLoading,
    refetch,
  } = useGetMyNotificationsQuery({
    cursor: cursorId,
  });

  useEffect(() => {
    setHasNextPage(notifications?.pagination.hasNextPage as boolean);
  }, [notifications?.pagination.hasNextPage]);

  const loadMore = useCallback(() => {
    setCursorId(notifications?.pagination.endCursor as string);
    setHasNextPage(notifications?.pagination.hasNextPage as boolean);
  }, [
    notifications?.pagination.endCursor,
    notifications?.pagination.hasNextPage,
  ]);

  const [sentryRef] = useInfiniteScroll({
    disabled: isError,
    hasNextPage: notifications?.pagination.hasNextPage as boolean,
    loading: isLoading,
    onLoadMore: loadMore,
    rootMargin: "0px 0px 400px 0px",
  });

  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onRead = async (item: NotificationUserItem) => {
    if (item.type !== NotificationUserType.MESSAGE) {
      onClose();
    }

    await readNotification(item.id);

    switch (item.type) {
      case NotificationUserType.FUMUFUMU_WAKARU:
      case NotificationUserType.NEW_COMMENT:
      case NotificationUserType.NEW_EVENT:
        Router.push(`/fumufumu/detail/${item.fumufumuId}`);
        break;
      case NotificationUserType.NEW_FOLLOWER:
      case NotificationUserType.NEW_LIKE_AVATAR:
        Router.push(`/other/${item.userId}`);
        break;
      case NotificationUserType.NEW_BADGE:
        Router.push(`/badge`);
        break;
      default:
        setDetailNotification(item);
        refetch();
        break;
    }
  };

  const onResetBadge = async () => {
    await onResetBadgeCount();
    setHasNextPage(false);
    setCursorId("");
  };

  return (
    <>
      <Modal
        opened={opened}
        onClose={onClose}
        withCloseButton={false}
        padding={10}
        radius={12}
        overlayOpacity={0.3}
      >
        <Flex direction={"row"} justify="space-between" align="center">
          <Text className={styles.modalHeader}>お知らせ一覧</Text>
          {badgeCount > 0 &&
            currentUser?.role !== "COACH" &&
            currentUser?.role !== "ADMIN" && (
              <Button onClick={onResetBadge}>すべて既読</Button>
            )}
        </Flex>
        <Divider />
        <ScrollArea>
          {notifications?.data?.map((item) => (
            <NotificationItem
              item={item}
              key={item.id}
              onClick={() => onRead(item)}
            />
          ))}
          {!isLoading && !notifications?.data?.length && (
            <Center sx={{ height: 100 }}>現在は通知がありません</Center>
          )}
          {(isLoading || hasNextPage) && (
            <Center ref={sentryRef} py={24}>
              <Loader size={24} />
            </Center>
          )}
        </ScrollArea>
        <Divider />
        <Space h={10} />
        <Flex align="center" w="100%" gap={8} justify="center">
          <Button className={styles.modalBackButton} onClick={onClose}>
            戻る
          </Button>
        </Flex>
      </Modal>
      <NotificationMessageModal
        item={detailNotification}
        opened={!!detailNotification}
        onClose={() => setDetailNotification(null)}
      />
    </>
  );
};

export const NotificationItem: FC<{
  item: NotificationUserItem;
  onClick: () => void;
}> = ({ item, onClick }) => {
  return (
    <Flex
      className={styles.notificationItem}
      direction={"column"}
      align="flex-start"
      gap={8}
      py={8}
      key={item.user.id}
      sx={{ position: "relative", cursor: "pointer" }}
      onClick={onClick}
    >
      <Flex
        align="flex-start"
        gap={12}
        sx={{ width: "fit-content", marginBottom: 3, paddingRight: 20 }}
      >
        <Flex sx={{ position: "relative" }}>
          <AvatarIconMemo
            avatar={item.user?.profileImage}
            background={item.user.profileBgImage}
          />
          <NotificationIcon type={item.type} />
        </Flex>
        <Flex direction={"column"}>
          <NotificationMessage notification={item} />
        </Flex>
      </Flex>
      <Flex
        sx={{
          position: "absolute",
          top: "20px",
          right: "5px",
          width: 10,
          height: 10,
          borderRadius: 5,
          background: item.read ? "transparent" : "#00A5B9",
        }}
      />
    </Flex>
  );
};

const NotificationIcon: FC<{ type: NotificationUserType }> = ({ type }) => {
  return (
    <Flex
      direction={"row"}
      justify={"center"}
      align="center"
      sx={{
        position: "absolute",
        background: "#fff",
        zIndex: 20,
        bottom: "-5px",
        right: "-5px",
        width: 20,
        height: 20,
        borderRadius: 10,
      }}
    >
      <Flex>
        {type === NotificationUserType.NEW_LIKE_AVATAR && (
          <BiLike size={"10px"} color="#00A5B9" />
        )}
        {type === NotificationUserType.FUMUFUMU_WAKARU && (
          <MdLightbulbOutline size={"10px"} color="#00A5B9" />
        )}
        {type === NotificationUserType.NEW_BADGE && (
          <MdOutlineMilitaryTech size={"10px"} color="#00A5B9" />
        )}
        {type === NotificationUserType.NEW_EVENT && (
          <MdEventAvailable size={"10px"} color="#00A5B9" />
        )}
        {type === NotificationUserType.NEW_FOLLOWER && (
          <RiUserFollowLine size={"10px"} color="#00A5B9" />
        )}
        {type === NotificationUserType.NEW_COMMENT && (
          <FaRegComment size={"10px"} color="#00A5B9" />
        )}
        {type === NotificationUserType.MESSAGE && (
          <MdMessage size={"10px"} color="#00A5B9" />
        )}
      </Flex>
    </Flex>
  );
};

export const NotificationListModalMemo = memo(NotificationListModal);
